import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, throwIfNotOk } from '../../lib/api';
import { qk } from '../../lib/queryKeys';
import { Card } from '@siliconx/shared/ui/Card';
import { PanelTitle } from '@siliconx/shared/ui/PanelTitle';

/**
 * Location / managing team / cabinet attributes card for the lot Overview
 * tab (Epic 1, "Lots as a First-Class Entity" — plans/lots-first-class.md).
 * Mirrors the asset detail page's LocationTeamNotesCard (EditableCards.tsx)
 * for visual/interaction consistency, but the write-path split is different
 * from that component — see migration 735's header:
 *
 *   - dataCenterId / internalLocation / isCompleteCabinet are ordinary
 *     client_readonly-granted columns — ANY member can edit them via
 *     PATCH /api/client/lots/:lotId.
 *   - teamId is a privilege column, NEVER in that PATCH's schema. It is set
 *     at creation (member's own team, forced server-side) and is only
 *     changeable afterwards by an ORG ADMIN, via PUT /api/client/lots/:lotId
 *     /team (client_assign_lot_team), and only while the lot has zero
 *     members — reassigning a non-empty lot's team would silently strand
 *     its current members' visibility (RLS is team-scoped for members).
 *     A non-admin always sees team as read-only. An admin sees a read-only
 *     value too UNLESS the lot is currently empty, in which case an edit
 *     control appears.
 */

interface LocationOption {
  id: string;
  code: string;
  name: string;
}

interface TeamOption {
  id: string;
  name: string;
}

export interface LotAttributesCardProps {
  lotId: string;
  dataCenterId: string | null;
  dataCenterName: string | null;
  internalLocation: string | null;
  isCompleteCabinet: boolean | null;
  teamId: string | null;
  teamName: string | null;
  /** True once the lot has zero members — the only state client_assign_lot_team allows a team change in. */
  isEmpty: boolean;
  /** org admin — required for any team edit at all. */
  canEditTeam: boolean;
  onSaved: () => void;
  notify: (message: string, type: 'success' | 'error') => void;
}

export function LotAttributesCard({
  lotId,
  dataCenterId,
  dataCenterName,
  internalLocation,
  isCompleteCabinet,
  teamId,
  teamName,
  isEmpty,
  canEditTeam,
  onSaved,
  notify,
}: LotAttributesCardProps) {
  const [editing, setEditing] = useState(false);
  const [selectedDataCenterId, setSelectedDataCenterId] = useState(dataCenterId ?? '');
  const [locationDraft, setLocationDraft] = useState(internalLocation ?? '');
  const [completeCabinetDraft, setCompleteCabinetDraft] = useState(isCompleteCabinet ?? false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Team edit is a separate, narrower control — only ever shown to an admin
  // on an empty lot, saved through a different endpoint than the three
  // fields above.
  const [editingTeam, setEditingTeam] = useState(false);
  const [selectedTeamId, setSelectedTeamId] = useState(teamId ?? '');
  const [teamSaving, setTeamSaving] = useState(false);

  useEffect(() => { setSelectedDataCenterId(dataCenterId ?? ''); }, [dataCenterId]);
  useEffect(() => { setLocationDraft(internalLocation ?? ''); }, [internalLocation]);
  useEffect(() => { setCompleteCabinetDraft(isCompleteCabinet ?? false); }, [isCompleteCabinet]);
  useEffect(() => { setSelectedTeamId(teamId ?? ''); }, [teamId]);

  const { data: locations } = useQuery<LocationOption[]>({
    queryKey: qk.locations(),
    queryFn: async () => {
      const res = await api.api.client.locations.$get();
      await throwIfNotOk(res);
      return (await res.json()) as unknown as LocationOption[];
    },
    enabled: editing,
  });
  const { data: teams } = useQuery<TeamOption[]>({
    queryKey: qk.teams(),
    queryFn: async () => {
      const res = await api.api.client.teams.$get();
      await throwIfNotOk(res);
      return (await res.json()) as unknown as TeamOption[];
    },
    enabled: canEditTeam && editingTeam,
  });

  async function handleSave() {
    setSaving(true);
    setSaveError(null);
    try {
      const res = await api.api.client.lots[':lotId'].$patch({
        param: { lotId },
        json: {
          dataCenterId: selectedDataCenterId || null,
          internalLocation: locationDraft.trim() !== '' ? locationDraft.trim() : null,
          isCompleteCabinet: completeCabinetDraft,
        },
      });
      await throwIfNotOk(res);
      setEditing(false);
      onSaved();
      notify('Lot attributes updated', 'success');
    } catch (ex: unknown) {
      setSaveError((ex as Error).message ?? 'Save failed.');
    } finally {
      setSaving(false);
    }
  }

  function handleCancel() {
    setSelectedDataCenterId(dataCenterId ?? '');
    setLocationDraft(internalLocation ?? '');
    setCompleteCabinetDraft(isCompleteCabinet ?? false);
    setSaveError(null);
    setEditing(false);
  }

  async function handleSaveTeam() {
    // teamId is required as of migration 738 (asset_lot.team_id NOT NULL) —
    // "unassign" is no longer a representable state; the Unassigned team
    // (always present per-org, migration 737) is just another option in
    // the select below. Guard client-side too, ahead of the server's own
    // {ok:false, reason:'team_required'} rejection.
    if (selectedTeamId === '') {
      notify('Choose a team first', 'error');
      return;
    }
    setTeamSaving(true);
    try {
      const res = await api.api.client.lots[':lotId'].team.$put({
        param: { lotId },
        json: { teamId: selectedTeamId },
      });
      await throwIfNotOk(res);
      setEditingTeam(false);
      onSaved();
      notify('Managing team updated', 'success');
    } catch (ex: unknown) {
      notify((ex as Error).message ?? 'Save failed.', 'error');
    } finally {
      setTeamSaving(false);
    }
  }

  return (
    <Card>
      <div className="flex items-center justify-between">
        <PanelTitle title="Location & team" />
        {!editing && (
          <button
            onClick={() => setEditing(true)}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            Edit
          </button>
        )}
      </div>

      {editing ? (
        <div className="mt-4 space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Data center
              </label>
              <select
                value={selectedDataCenterId}
                onChange={(e) => setSelectedDataCenterId(e.target.value)}
                className="mt-1 w-full rounded border border-border px-3 py-1.5 text-sm text-foreground focus:border-blue-500 focus:outline-none"
              >
                <option value="">— none —</option>
                {(locations ?? []).map((l) => (
                  <option key={l.id} value={l.id}>{l.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Internal location
              </label>
              <input
                type="text"
                value={locationDraft}
                onChange={(e) => setLocationDraft(e.target.value)}
                placeholder="e.g. R14-C03"
                maxLength={160}
                className="mt-1 w-full rounded border border-border px-3 py-1.5 text-sm text-foreground focus:border-blue-500 focus:outline-none"
              />
            </div>
            <div className="flex items-center gap-2 sm:col-span-2">
              <input
                id="lot-complete-cabinet"
                type="checkbox"
                checked={completeCabinetDraft}
                onChange={(e) => setCompleteCabinetDraft(e.target.checked)}
                className="h-4 w-4 rounded border-border text-brand-600 focus:ring-brand-500"
              />
              <label htmlFor="lot-complete-cabinet" className="text-sm text-foreground">
                This lot represents a complete cabinet
              </label>
            </div>
          </div>

          {saveError && <p className="text-sm text-red-600">{saveError}</p>}

          <div className="flex items-center gap-3">
            <button
              onClick={() => void handleSave()}
              disabled={saving}
              className="rounded bg-blue-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button
              onClick={handleCancel}
              disabled={saving}
              className="text-sm text-muted-foreground hover:text-foreground disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <dl className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3">
          <div>
            <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Data center
            </dt>
            <dd className="mt-1 text-sm text-foreground">{dataCenterName ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Internal location
            </dt>
            <dd className="mt-1 text-sm text-foreground">{internalLocation ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Complete cabinet
            </dt>
            <dd className="mt-1 text-sm text-foreground">
              {isCompleteCabinet === null ? '—' : isCompleteCabinet ? 'Yes' : 'No'}
            </dd>
          </div>
        </dl>
      )}

      {/* Managing team — separate write path, admin + empty-lot gated (see
          header comment). Everyone else, and an admin on a non-empty lot,
          gets a plain read-only value with an explanatory note. */}
      <div className="mt-4 border-t border-border pt-4">
        <div className="flex items-center justify-between">
          <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Managing team
          </dt>
          {canEditTeam && isEmpty && !editingTeam && (
            <button
              onClick={() => setEditingTeam(true)}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              Change
            </button>
          )}
        </div>
        {editingTeam ? (
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <select
              value={selectedTeamId}
              onChange={(e) => setSelectedTeamId(e.target.value)}
              className="rounded border border-border px-3 py-1.5 text-sm text-foreground focus:border-blue-500 focus:outline-none"
            >
              {/* No "unassigned" placeholder — team_id is required (738).
                  The org's own Unassigned team appears in this list like any
                  other team; pick it explicitly to represent "no real team". */}
              <option value="" disabled>— choose a team —</option>
              {(teams ?? []).map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
            <button
              onClick={() => void handleSaveTeam()}
              disabled={teamSaving || selectedTeamId === ''}
              className="rounded bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {teamSaving ? 'Saving…' : 'Save'}
            </button>
            <button
              onClick={() => { setSelectedTeamId(teamId ?? ''); setEditingTeam(false); }}
              disabled={teamSaving}
              className="text-sm text-muted-foreground hover:text-foreground disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        ) : (
          <dd className="mt-1 text-sm text-foreground">
            {teamName ?? <span className="text-muted-foreground">Unassigned</span>}
            {canEditTeam && !isEmpty && (
              <span className="ml-2 text-xs text-muted-foreground">
                (remove all assets to reassign)
              </span>
            )}
          </dd>
        )}
      </div>
    </Card>
  );
}
